import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import Layout from '../component/layout'
import { toastify } from '../../../utils/toast'
import { getItem } from '../../../utils/localStorage'
import { makeRequest2 } from '../../../utils/makeRequest'
import { createTicketURL } from '../../../utils/urls'

const RaiseTicket = () => {
    const [ticketType, setTicketType] = useState("");
    const [subject, setSubject] = useState("");
    const [description, setDescription] = useState("");
    const [priority, setPriority] = useState("");
    const [images, setImages] = useState([]);
    const [imagePreviews, setImagePreviews] = useState([]);
    const [date, setDate] = useState(new Date());
    const [fullName, setFullName] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');

    const getCurrentUser = async () => {
        try {
            const currentUserValue = await getItem("APPUSER");
            let currentUser;
            // Check if the value needs to be parsed
            if (typeof currentUserValue === "string") {
                try {
                    currentUser = JSON.parse(currentUserValue);
                } catch (err) {
                    //console.error("Error parsing APPUSER:", err);
                }
            } else {
                currentUser = currentUserValue; // Assume it's already an object
            }
            // Safely access and set user details
            if (currentUser?.user) {
                const fullName = currentUser.user.fullName;
                const phoneNumber = currentUser.user.phoneNumber;
                setFullName(fullName);
                setPhoneNumber(phoneNumber);

            } else {
                console.error("Invalid user structure:", currentUser);
            }
        } catch (err) {
            console.log(err.message);
        }
    };
    // Handle multiple file selection
    const handleImageChange = (e) => {
        const newFiles = Array.from(e.target.files);
        const maxFiles = 5;
        // Check if adding new files would exceed the limit
        if (images.length + newFiles.length > maxFiles) {
            toastify(`Maximum ${maxFiles} images allowed. You can add ${maxFiles - images.length} more.`, 'error');
            return;
        }
        // Validate file types and sizes
        const validFiles = newFiles.filter(file => {
            const isValidType = file.type.startsWith('image/');
            const isValidSize = file.size <= 5 * 1024 * 1024; // 5MB limit
            if (!isValidType) {
                toastify(`${file.name} is not a valid image file`, 'error');
                return false;
            }
            if (!isValidSize) {
                toastify(`${file.name} is too large (max 5MB)`, 'error');
                return false;
            }
            return true;
        });
        // Add new files to existing images array (instead of replacing)
        setImages(prevImages => [...prevImages, ...validFiles]);
        // Create previews for new files and add to existing previews
        const newPreviews = validFiles.map(file => ({
            file,
            url: URL.createObjectURL(file),
            name: file.name
        }));

        setImagePreviews(prevPreviews => [...prevPreviews, ...newPreviews]);
        // Clear the input so the same files can be selected again if needed
        e.target.value = '';
    };
    // Helper function to clear all images
    const clearAllImages = () => {
        // Clean up all object URLs
        imagePreviews.forEach(preview => {
            URL.revokeObjectURL(preview.url);
        });
        setImages([]);
        setImagePreviews([]);
    };
    // Updated removeImage function (same as before but shown for completeness)
    const removeImage = (index) => {
        const newImages = images.filter((_, i) => i !== index);
        const newPreviews = imagePreviews.filter((_, i) => i !== index);
        // Clean up object URL
        URL.revokeObjectURL(imagePreviews[index].url);

        setImages(newImages);
        setImagePreviews(newPreviews);
    };

    const handleSubmit = async () => {
        try {
            const facilityId = await getItem('selectedFacilityId');
            const userId = localStorage.getItem("userId");

            if (!subject) throw new Error('Please select a subject');
            if (!description) throw new Error('Please enter a description');
            // Create FormData
            const formData = new FormData();
            formData.append('ticketType', ticketType);
            formData.append('subject', subject);
            formData.append('description', description);
            formData.append('priority', priority);
            formData.append('date', date);
            formData.append('userId', userId);
            formData.append('fullName', fullName);
            formData.append('phoneNumber', phoneNumber);
            // Append multiple images
            if (images.length > 0) {
                images.forEach((image, index) => {
                    console.log(`Appending image ${index}:`, image);
                    formData.append('images', image); // Note: 'images' plural
                });
            }
            // Debug FormData
            for (let pair of formData.entries()) {
                console.log(pair[0] + ':', pair[1]);
            }

            const response = await makeRequest2(
                `${createTicketURL}/${userId}/${facilityId}`,
                'POST',
                formData,
            );

            if (response.success) {
                toastify('Ticket raised successfully', 'success');
                handleClear();
            } else {
                console.log(response.error);
                throw new Error(response.error);
            }
        } catch (err) {
            toastify(err.message, 'error');
        }
    };

    const handleClear = () => {
        setTicketType('');
        setSubject('');
        setDescription('');
        setPriority('');
        setDate(new Date());
        imagePreviews.forEach(preview => {
            URL.revokeObjectURL(preview.url);
        });
        // Reset states
        setImages([]);
        setImagePreviews([]);
    }

    const renderDynamicFields = () => {
        const renderPrioritySection = () => (
            <div className="mb-3">
                <label className="form-label fw-bold">Priority</label>
                <div className="form-check">
                    <input
                        className="form-check-input"
                        type="radio"
                        name="priority"
                        id="low"
                        value="Low"
                        onChange={(e) => setPriority(e.target.value)}
                        checked={priority === "Low"}
                    />
                    <label className="form-check-label" htmlFor="low">Low</label>
                </div>
                <div className="form-check">
                    <input
                        className="form-check-input"
                        type="radio"
                        name="priority"
                        id="medium"
                        value="Medium"
                        onChange={(e) => setPriority(e.target.value)}
                        checked={priority === "Medium"}
                    />
                    <label className="form-check-label" htmlFor="medium">Medium</label>
                </div>
                <div className="form-check">
                    <input
                        className="form-check-input"
                        type="radio"
                        name="priority"
                        id="high"
                        value="High"
                        onChange={(e) => setPriority(e.target.value)}
                        checked={priority === "High"}
                    />
                    <label className="form-check-label" htmlFor="high">High</label>
                </div>
            </div>
        );



        switch (ticketType) {
            case "Complaints":
                return (
                    <>
                        <div className="mb-4">
                            <label className="form-label fw-bold">
                                <i className="bi bi-tag"></i> Subject
                            </label>
                            <select
                                className="form-select"
                                value={subject}
                                onChange={(e) => setSubject(e.target.value)}
                            >
                                <option value="" disabled>-- Select a complaint type --</option>
                                <option value="Noise Levels">Noise Levels</option>
                                <option value="Cleanliness">Cleanliness</option>
                                <option value="Neighbour Issues">Neighbour Issues</option>
                                <option value="Parking">Parking</option>
                                <option value="Infestation">Infestation</option>
                                <option value="Other">Other</option>
                            </select>
                        </div>
                        <div className="mb-4">
                            <label className="form-label fw-bold">
                                <i className="bi bi-chat-dots"></i> Description
                            </label>
                            <textarea
                                className="form-control"
                                rows={5}
                                placeholder="Please describe your complaint in detail..."
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                            ></textarea>
                        </div>
                        {renderPrioritySection()}
                    </>
                );
            case "Maintenance":
                return (
                    <>
                        <div className="mb-4">
                            <label className="form-label fw-bold">
                                <i className="bi bi-tools"></i> Subject
                            </label>
                            <select
                                className="form-select"
                                value={subject}
                                onChange={(e) => setSubject(e.target.value)}
                            >
                                <option value="" disabled>-- Select a maintenance type --</option>
                                <option value="General Maintenance">General Maintenance</option>
                                <option value="Preventive Maintenance">Preventive Maintenance</option>
                                <option value="Emergency Maintenance">Emergency Maintenance</option>
                            </select>
                        </div>
                        <div className="mb-4">
                            <label className="form-label fw-bold">
                                <i className="bi bi-chat-dots"></i> Description
                            </label>
                            <textarea
                                className="form-control"
                                rows={5}
                                placeholder="Please describe the maintenance issue in detail..."
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                            ></textarea>
                        </div>
                        {/* <div className="mb-4">
                            <label className="form-label fw-bold">
                                <i className="bi bi-camera"></i> Upload Photo (Optional)
                            </label>
                            <input
                                type="file"
                                className="form-control"
                                onChange={(e) => setImage(e.target.files[0])}
                            />
                            <div className="form-text">Attach a photo for better understanding of the issue.</div>
                        </div> */}

                        <div className="mb-4">
                            <label className="form-label fw-bold">
                                <i className="bi bi-camera"></i> Upload Photo(s) (Optional)
                                {images.length > 0 && (
                                    <span className="badge bg-primary ms-2">
                                        {images.length}/5
                                    </span>
                                )}
                            </label>
                            <input
                                type="file"
                                className="form-control"
                                multiple
                                accept="image/*"
                                onChange={handleImageChange}
                            />
                            <div className="form-text">
                                Attach up to 5 photos for better understanding of the issue. Max 5MB per image.
                                {images.length > 0 && (
                                    <span className="text-success">
                                        {` Currently selected: ${images.length} image(s)`}
                                    </span>
                                )}
                            </div>

                            {/* Image Previews */}
                            {imagePreviews.length > 0 && (
                                <div className="mt-3">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <small className="text-muted">Selected Images:</small>
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary btn-sm"
                                            onClick={clearAllImages}
                                        >
                                            Clear All
                                        </button>
                                    </div>
                                    <div className="row g-2">
                                        {imagePreviews.map((preview, index) => (
                                            <div key={index} className="col-md-3">
                                                <div className="position-relative">
                                                    <img
                                                        src={preview.url}
                                                        alt={`Preview ${index + 1}`}
                                                        className="img-thumbnail w-100"
                                                        style={{ height: '100px', objectFit: 'cover' }}
                                                    />
                                                    <button
                                                        type="button"
                                                        className="btn btn-danger btn-sm position-absolute top-0 end-0"
                                                        style={{ transform: 'translate(25%, -25%)' }}
                                                        onClick={() => removeImage(index)}
                                                    >
                                                        <i className="ti ti-x"></i>
                                                    </button>
                                                    <div className="small text-muted mt-1 text-truncate">
                                                        {preview.name}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                        {renderPrioritySection()}
                    </>
                );
            default:
                return null;
        }

    };

    useEffect(() => {
        getCurrentUser();
    }, []);

    return (
        <Layout>
            <div className="page-header">
                <div className="page-block">
                    <div className="row align-items-center">
                        <div className="col-md-12">
                            <ul className="breadcrumb">
                                <li className="breadcrumb-item">
                                    <Link to={"/resident/"}>Dashboard</Link>
                                </li>
                                <li className="breadcrumb-item">
                                    <Link to={"/resident/raise_ticket"}>Raise Ticket</Link>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row">
                <div className="col-12">
                    <div className="card">
                        <div className="card-header">
                            <h5>Raise a Ticket</h5>
                        </div>
                        <div className="card-body">
                            <div className="mb-3">
                                <label className="form-label fw-bold">Ticket Type</label>
                                <select className="form-control"
                                    value={ticketType}
                                    onChange={(e) => setTicketType(e.target.value)}
                                >
                                    <option value="">-- Select --</option>
                                    <option value="Complaints">Complaints</option>
                                    <option value="Maintenance">Maintenance</option>
                                </select>
                            </div>
                            {renderDynamicFields()}
                        </div>
                        <div className="card-footer text-end btn-page">
                            <button className="btn btn-outline-secondary"
                                onClick={handleClear}
                            >Clear</button>
                            <button className="btn btn-primary"
                                onClick={handleSubmit}
                            >Submit</button>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    )
}

export default RaiseTicket